import { createFileRoute } from "@tanstack/react-router";
import { useCallback, useState } from "react";
import { useForm } from "@/hooks/useForm";
import { changePasswordSchema } from "@shared/validation/composites/auth-fields";
import { useTranslation } from "react-i18next";
import { useNavigate } from "@tanstack/react-router";
import { Card, CardHeader } from "@/components/ui/card";
import { CardTitle, Text } from "@/components/ui/text";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { InputWrapper } from "@/components/ui/input-wrapper";
import { FormLabel } from "@/components/ui/form-label";
import { PageNavigationHeader } from "@/components/ui/page-navigation-header";
import { SettingsPageLayout } from "@/components/ui/settings-page-layout";
import { Surface } from "@/components/ui/surface";
import { useToast } from "@/components/ui/toast";
import { VStack } from "@/components/ui/v-stack";
import { HStack } from "@/components/ui/h-stack";
import { Icon } from "@/components/ui/icons";
import { PasswordRequirements } from "@/components/ui/password-requirements";
import { ConfirmDialog } from "@/components/ui/confirm-dialog";
import { changePassword } from "@/lib/api/users";
import { logoutAllDevices } from "@/lib/api/auth";

type PasswordFormState = {
  currentPassword: string;
  newPassword: string;
  confirmPassword: string;
};

function SecurityPage() {
  const navigate = useNavigate();
  const { t: tCommon } = useTranslation("common");
  const { t: tSecurity } = useTranslation("security");
  const { t: tSettings } = useTranslation("settings");
  const { addToast } = useToast();

  const [showSignOutAllConfirm, setShowSignOutAllConfirm] = useState(false);
  const [isSigningOutAll, setIsSigningOutAll] = useState(false);

  const handleSignOutAllDevices = useCallback(async () => {
    setIsSigningOutAll(true);
    try {
      await logoutAllDevices();
      setShowSignOutAllConfirm(false);
      await navigate({ to: "/login" });
    } catch {
      addToast(tSecurity("signOutAllDevicesFailed"), "error");
    } finally {
      setIsSigningOutAll(false);
    }
  }, [navigate, addToast, tSecurity]);

  const form = useForm<PasswordFormState>({
    schema: changePasswordSchema(),
    defaults: { currentPassword: "", newPassword: "", confirmPassword: "" },
    onSubmit: async (data) => {
      await changePassword({
        currentPassword: data.currentPassword,
        newPassword: data.newPassword,
      });
      form.reset();
      addToast(tSecurity("passwordChangedSuccess"), "success");
    },
  });

  const handlePasswordChange = useCallback(
    async (e: React.FormEvent) => {
      e.preventDefault();
      await form.submitSafe();
    },
    [form],
  );

  return (
    <VStack gap="xl" data-testid="security-page">
      <PageNavigationHeader
        title={tSettings("security")}
        contextLabel={tSettings("account")}
        metadata={tSecurity("description")}
        onBack={() => void navigate({ to: "/account" })}
        backAriaLabel={tCommon("back")}
        contextAriaLabel={tSettings("account")}
      />

      <SettingsPageLayout variant="form" className="space-y-6">
        {/* Password Change */}
        <Card padding="none" data-testid="password-change-card">
          <CardHeader>
            <CardTitle>{tSecurity("changePassword")}</CardTitle>
          </CardHeader>
          <form onSubmit={handlePasswordChange} className="p-6 space-y-4">
            <VStack gap="none">
              <FormLabel htmlFor="current-password">
                {tSecurity("currentPassword")}
              </FormLabel>
              <InputWrapper error={form.fieldError("currentPassword")}>
                <Input
                  id="current-password"
                  type="password"
                  value={form.fields.currentPassword}
                  onChange={(e) =>
                    form.setField("currentPassword", e.target.value)
                  }
                  onBlur={() => form.touchField("currentPassword")}
                  required
                  data-testid="current-password-input"
                />
              </InputWrapper>
            </VStack>
            <VStack gap="none">
              <FormLabel htmlFor="new-password">
                {tSecurity("newPassword")}
              </FormLabel>
              <InputWrapper error={form.fieldError("newPassword")}>
                <Input
                  id="new-password"
                  type="password"
                  value={form.fields.newPassword}
                  onChange={(e) => form.setField("newPassword", e.target.value)}
                  onBlur={() => form.touchField("newPassword")}
                  required
                  minLength={8}
                  data-testid="new-password-input"
                />
              </InputWrapper>
              <PasswordRequirements
                value={form.fields.newPassword}
                show={
                  !!form.fieldError("newPassword") ||
                  form.fields.newPassword.length > 0
                }
              />
            </VStack>
            <VStack gap="none">
              <FormLabel htmlFor="confirm-password">
                {tSecurity("confirmNewPassword")}
              </FormLabel>
              <InputWrapper error={form.fieldError("confirmPassword")}>
                <Input
                  id="confirm-password"
                  type="password"
                  value={form.fields.confirmPassword}
                  onChange={(e) =>
                    form.setField("confirmPassword", e.target.value)
                  }
                  onBlur={() => form.touchField("confirmPassword")}
                  required
                  minLength={8}
                  data-testid="confirm-password-input"
                />
              </InputWrapper>
            </VStack>
            {form.serverErrors._form || form.status === "error" ? (
              <Text
                color="danger"
                size="sm"
                data-testid="change-password-error"
              >
                {form.serverErrors._form ?? tCommon("error")}
              </Text>
            ) : null}
            <Button
              type="submit"
              disabled={!form.isValid || form.isSubmitting}
              data-testid="change-password-button"
            >
              {form.isSubmitting
                ? tSecurity("changingPassword")
                : tSecurity("changePassword")}
            </Button>
          </form>
        </Card>

        {/* Active Sessions */}
        <Card padding="none" data-testid="active-sessions-card">
          <CardHeader>
            <CardTitle>{tSecurity("activeSessions")}</CardTitle>
          </CardHeader>
          <VStack gap="none" padding="lg">
            <VStack>
              <Surface bg="container" rounded="lg" padding="lg">
                <HStack justify="between" gap="none">
                  <HStack gap="lg">
                    <Icon.Computer color="muted" />
                    <VStack gap="none">
                      <Text weight="medium">{tSecurity("currentSession")}</Text>
                      <Text color="muted">{tSecurity("activeNow")}</Text>
                    </VStack>
                  </HStack>
                  <Text size="xs" color="success" weight="medium">
                    {tSecurity("thisDevice")}
                  </Text>
                </HStack>
              </Surface>
            </VStack>
            <Text color="muted" className="mt-4">
              {tSecurity("singleDeviceSignedIn")}
            </Text>
            <VStack gap="none" className="mt-4">
              <Button
                color="danger"
                variant="outline"
                onClick={() => setShowSignOutAllConfirm(true)}
                data-testid="sign-out-all-devices-button"
              >
                {tSecurity("signOutAllDevices")}
              </Button>
              <Text color="muted" size="xs" className="mt-2">
                {tSecurity("signOutAllDevicesDescription")}
              </Text>
            </VStack>
          </VStack>
        </Card>
      </SettingsPageLayout>

      <ConfirmDialog
        open={showSignOutAllConfirm}
        title={tSecurity("signOutAllDevicesConfirmTitle")}
        description={tSecurity("signOutAllDevicesConfirmDescription")}
        confirmLabel={tSecurity("signOutAllDevicesConfirmButton")}
        cancelLabel={tCommon("cancel")}
        confirmColor="danger"
        loading={isSigningOutAll}
        onConfirm={() => void handleSignOutAllDevices()}
        onCancel={() => setShowSignOutAllConfirm(false)}
      />
    </VStack>
  );
}

export const Route = createFileRoute("/_app/account/security")({
  component: SecurityPage,
});
